'use client';

import { useEffect, useState, useRef } from 'react';
import { useScheduler } from '../SchedulerContext';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Button } from '@heroui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheckCircle } from 'react-icons/fa';
import { sendQuote } from '../utils/sendQuote';
import { useTZDayjs } from '@/context/TimezoneContext';
import { useWhatConvertsEvents } from '../hooks/useWhatConvertsEvents';
import posthog from 'posthog-js';
import { EVENTS } from '@attik/posthog';

interface SendQuoteLoaderProps {
  onComplete: (quoteSlug: string) => void;
  onError: (error: string) => void;
  onRetry?: () => void;
}

const QUOTE_PUNS = ['Preparing your quote...', 'Sending to client...', 'Finalizing details...'];

export default function SendQuoteLoader({ onComplete, onError, onRetry }: SendQuoteLoaderProps) {
  const {
    address,
    selectedDateTime,
    contact,
    selectedRole,
    additionalContacts,
    requiredInfoValues,
    equipmentQuantities,
    priceData,
    draftQuoteId,
    draftQuoteSlug,
    selectedSlotInspectorInfo,
    selectedInspectorIdsForFilter,
    scheduleSettings,
    selectedBundle,
    bundles,
    appliedDiscountCode,
    appliedDiscountServiceIds,
    workingHold,
    jwtToken,
    companyId,
    wcLeadId,
    setDraftQuoteId,
    setDraftQuoteSlug,
    setWorkingHold,
    setWorkingHoldExpiresAt,
    finalServices,
  } = useScheduler();

  const tzDayjs = useTZDayjs();
  const { updateLead } = useWhatConvertsEvents();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [currentPunIndex, setCurrentPunIndex] = useState(0);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const hasSentRef = useRef(false);

  // Rotate puns every 1 second
  useEffect(() => {
    if (status !== 'loading') return;

    const interval = setInterval(() => {
      setCurrentPunIndex((prevIndex) => (prevIndex + 1) % QUOTE_PUNS.length);
    }, 1000);

    return () => clearInterval(interval);
  }, [status]);

  // Countdown timer for redirect
  useEffect(() => {
    if (status !== 'success') return;

    const interval = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [status]);

  // Handle quote sending - use ref to ensure it only runs once
  const sendQuoteHandler = async () => {
    // Guard against double execution
    if (hasSentRef.current) return;
    hasSentRef.current = true;

    try {
      // Validate required fields
      if (!draftQuoteId) {
        throw new Error('No quote found. Please start a new quote.');
      }

      if (!address || !priceData) {
        throw new Error('Please complete all required fields before sending the quote.');
      }

      // Call sendQuote utility
      const updatedQuote = await sendQuote({
        draftQuoteId,
        draftQuoteSlug,
        address,
        priceData,
        contact,
        selectedRole,
        additionalContacts,
        requiredInfoValues,
        equipmentQuantities,
        selectedDateTime,
        selectedSlotInspectorInfo,
        selectedInspectorIdsForFilter,
        scheduleSettings,
        selectedBundle,
        bundles,
        appliedDiscountCode,
        appliedDiscountServiceIds,
        workingHold,
        createHold: false, // Don't create a quote hold for online scheduler
        wcLeadId: wcLeadId || undefined,
        jwtToken,
        companyId,
        tzDayjs,
        setDraftQuoteId,
        setDraftQuoteSlug,
        setWorkingHold,
        setWorkingHoldExpiresAt,
      });

      // Success!
      setStatus('success');

      posthog.capture(EVENTS.schedulerQuoteSent, {
        company_id: companyId,
        services: finalServices?.map((s) => s.name),
        service_count: finalServices?.length,
        total_price: priceData?.totalPrice,
        total_duration_hours: priceData?.totalDuration,
        discount_code: appliedDiscountCode?.code ?? null,
        scheduled_date: selectedDateTime,
      });

      // Update WhatConverts lead with quote value (fire-and-forget)
      if (wcLeadId && priceData?.totalPrice) {
        updateLead(wcLeadId, { quoteValue: priceData.totalPrice }).catch((error) => {
          console.error('[SendQuoteLoader] Failed to update WC lead:', error);
        });
      }

      // Get slug for callback
      const slug = updatedQuote?.slug;
      if (slug) {
        onComplete(slug);
      }

      // Note: sendQuote utility handles the redirect internally
      // But we'll show success message here as well
    } catch (error: any) {
      console.error('Error sending quote:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Failed to send quote');
      onError(error.message || 'Failed to send quote');
    }
  };

  // Call sending function immediately on mount, guarded by ref
  if (!hasSentRef.current) {
    sendQuoteHandler();
  }

  return (
    <div className='mx-auto flex max-w-5xl flex-col items-center justify-center py-8'>
      {/* Loading State */}
      {status === 'loading' && (
        <>
          <div style={{ width: '600px', height: '375px' }}>
            <DotLottieReact
              src='https://lottie.host/4f8822fd-2c97-458a-b375-ea12dc356d84/CDAl7cfiK7.lottie'
              loop
              speed={0.3}
              autoplay
            />
          </div>
          <div className='mt-4 flex h-12 items-center justify-center'>
            <AnimatePresence mode='wait'>
              <motion.p
                key={currentPunIndex}
                initial={{ rotateX: 90, opacity: 0 }}
                animate={{ rotateX: 0, opacity: 1 }}
                exit={{ rotateX: -90, opacity: 0 }}
                transition={{
                  duration: 0.5,
                  ease: 'easeInOut',
                }}
                className='text-xl font-medium text-gray-700'
              >
                {QUOTE_PUNS[currentPunIndex]}
              </motion.p>
            </AnimatePresence>
          </div>
        </>
      )}

      {/* Success State */}
      {status === 'success' && (
        <div className='text-center'>
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15,
              duration: 0.5,
            }}
            className='mb-6 flex justify-center'
          >
            <FaCheckCircle className='text-success' size={120} />
          </motion.div>
          <h2 className='mb-2 text-2xl font-bold text-gray-800'>Quote Sent Successfully!</h2>
          <p className='text-gray-600'>
            Redirecting to quote portal
            {redirectCountdown > 0
              ? ` in ${redirectCountdown} second${redirectCountdown !== 1 ? 's' : ''}...`
              : '...'}
          </p>
        </div>
      )}

      {/* Error State */}
      {status === 'error' && (
        <div className='text-center'>
          <div style={{ maxWidth: '300px', margin: '0 auto' }}>
            <DotLottieReact
              src='https://lottie.host/63049464-e5ed-4a06-a1d6-d26f0c21859b/UoohR1Pp6C.lottie'
              speed={0.5}
              autoplay
            />
          </div>
          <h2 className='mb-2 text-2xl font-bold text-gray-800'>Error Sending Quote</h2>
          <p className='mb-4 text-gray-600'>{errorMessage}</p>
          {onRetry && (
            <Button color='danger' onPress={onRetry} className='font-bold uppercase'>
              Try Again
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
